"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import type { SearchHit } from "@/lib/listings";
import { formatPrice } from "@/lib/format";
import FavoriteButton from "./FavoriteButton";
import type { MapBounds } from "./SearchMap";

const SearchMap = dynamic(() => import("./SearchMap"), { ssr: false });

type Props = {
  results: SearchHit[];
  favoriteIds: string[];
  signedIn: boolean;
  searchSuffix?: string;
  displayCurrency?: string | null;
  /** Nights span being searched, used to compute the 'X for N nights' total
      line in the row-card foot. Optional; falls back to per-night only. */
  nights?: number;
};

/**
 * Search results — two-pane shell per the prototype's
 * 'Search Results.html': horizontal row-cards on the left, sticky
 * map panel on the right (visible from 1100px+). Row-cards use the
 * prototype's `.row-card` markup with serif title, where-eyebrow,
 * rating, amenity row, and dashed-rule foot with price + cancel pill.
 */
export default function SearchResults({
  results,
  favoriteIds,
  signedIn,
  searchSuffix = "",
  displayCurrency = null,
  nights = 0,
}: Props) {
  const favSet = new Set(favoriteIds);
  // Mobile-only: the map sits above the listings with a drag handle.
  // Tapping the handle toggles it; pointer-dragging resizes it.
  // Desktop ignores this state and always renders side-by-side via CSS.
  const [mapHidden, setMapHidden] = useState(false);
  const [mapHeightPx, setMapHeightPx] = useState<number | null>(null);
  const dragStart = useRef<{ y: number; h: number } | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  // Bounds reported by the Google Map. When set, we filter the list
  // (and the count) to only listings whose lat/lng falls inside.
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const onBoundsChange = useCallback((b: MapBounds) => {
    setBounds(b);
  }, []);
  const visibleResults = useMemo(() => {
    // When the map is hidden, ignore stale bounds — the user can't
    // re-pan to fix them, so we'd otherwise show "no places in map
    // area" forever just because the panel is collapsed.
    if (!bounds || mapHidden) return results;
    return results.filter(
      (r) =>
        r.latitude >= bounds.south &&
        r.latitude <= bounds.north &&
        r.longitude >= bounds.west &&
        r.longitude <= bounds.east,
    );
  }, [bounds, results, mapHidden]);

  // Drag-to-resize the map panel on mobile. PointerEvents handle both
  // touch and mouse. We snap on release to: hidden / normal (50vh) /
  // expanded (85vh) so the user lands at a sensible height.
  function onHandlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (typeof window === "undefined" || window.innerWidth > 1100) return;
    const panel = e.currentTarget.parentElement as HTMLElement | null;
    if (!panel) return;
    panelRef.current = panel;
    dragStart.current = { y: e.clientY, h: panel.getBoundingClientRect().height };
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function onHandlePointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (!dragStart.current) return;
    const delta = e.clientY - dragStart.current.y;
    const next = Math.max(0, dragStart.current.h + delta);
    setMapHeightPx(next);
  }
  function onHandlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    if (!dragStart.current) return;
    const vh = window.innerHeight;
    const final = mapHeightPx ?? dragStart.current.h;
    // Snap to nearest of hidden / 50vh / 85vh — Airbnb-style three-stop.
    // If almost no drag happened (a tap), toggle hide instead.
    const dragged = Math.abs(final - dragStart.current.h) > 12;
    if (!dragged) {
      setMapHidden(true);
      setMapHeightPx(null);
    } else if (final < vh * 0.22) {
      setMapHidden(true);
      setMapHeightPx(null);
    } else if (final > vh * 0.7) {
      setMapHeightPx(Math.round(vh * 0.85));
    } else {
      setMapHeightPx(Math.round(vh * 0.5));
    }
    dragStart.current = null;
    panelRef.current = null;
    (e.currentTarget as Element).releasePointerCapture(e.pointerId);
  }

  // Header line. When bounds are active and have narrowed the list,
  // we say "N homes within map area"; otherwise just "N places".
  // If the map is hidden the bounds are stale and shouldn't drive
  // the label any more — fall back to the plain count.
  const countLabel = bounds && !mapHidden
    ? `${visibleResults.length} home${visibleResults.length === 1 ? "" : "s"} within map area`
    : `${visibleResults.length} place${visibleResults.length === 1 ? "" : "s"}`;

  return (
    <div className={`results-shell${mapHidden ? " map-hidden-mobile" : ""}`}>
      <div className="results-col">
        <button
          type="button"
          className="show-map-btn"
          onClick={() => setMapHidden(false)}
          aria-label="Show map"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}>
            <path d="M6 2l-4 1.5v11l4-1.5 4 1.5 4-1.5v-11l-4 1.5L6 2z" strokeLinejoin="round" />
            <path d="M6 2v11M10 3.5v11" />
          </svg>
          Show map
        </button>
        <div className="results-count-inline">{countLabel}</div>
        {visibleResults.length === 0 && bounds ? (
          <div className="results-empty-bounds">
            <p>No stays in this map area.</p>
            <button
              type="button"
              className="btn-pill outline sm"
              onClick={() => setBounds(null)}
            >
              Reset map
            </button>
          </div>
        ) : null}
        {visibleResults.map((l) => {
          const href = `/listings/${l.slug ?? l.id}${searchSuffix}`;
          const total = nights > 0 ? l.pricePerNight * nights : 0;
          const skipOptimize = !!l.photoUrl && /^http:\/\/(localhost|127\.0\.0\.1)/.test(l.photoUrl);
          const isJustListed = l.createdAt
            ? Date.now() - new Date(l.createdAt).getTime() < 14 * 24 * 60 * 60 * 1000
            : false;
          return (
            <a key={l.id} href={href} className="row-card">
              <div className="row-thumb">
                {l.photoUrlSm || l.photoUrlMd || l.photoUrl ? (
                  <Image
                    src={l.photoUrlSm ?? l.photoUrlMd ?? l.photoUrl ?? ""}
                    alt={l.title}
                    fill
                    sizes="(max-width: 720px) 100vw, 220px"
                    className="object-cover"
                    unoptimized={skipOptimize}
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-[color:var(--ink-4)] text-xs">
                    no photo
                  </div>
                )}
                {l.isSuperhost ? (
                  <span className="row-pill super">Superhost</span>
                ) : isJustListed ? (
                  <span className="row-pill fresh">Just listed</span>
                ) : null}
                {signedIn ? (
                  <span className="row-fav" onClick={(e) => e.preventDefault()}>
                    <FavoriteButton listingId={l.id} isFavorite={favSet.has(l.id)} />
                  </span>
                ) : null}
              </div>
              <div className="row-info">
                <div className="row-where">
                  {l.city}
                  {l.country ? ` · ${l.country}` : ""}
                </div>
                <div className="row-title">{l.title}</div>
                <div className="row-rating">
                  {l.rating != null ? (
                    <>
                      <span className="star">★</span> {l.rating.toFixed(1)}{" "}
                      <span className="count">({l.reviewCount} review{l.reviewCount === 1 ? "" : "s"})</span>
                    </>
                  ) : (
                    <>
                      <span className="star">★</span> —{" "}
                      <span className="count">new · 0 reviews</span>
                    </>
                  )}
                </div>
                {l.distanceKm != null ? (
                  <div className="row-amen">
                    <span>{l.distanceKm.toFixed(1)} km away</span>
                  </div>
                ) : null}
                <div className="row-foot">
                  <div className="row-price">
                    <span className="nightly">
                      <strong>{formatPrice(l.pricePerNight, l.currency, displayCurrency)}</strong>{" "}
                      <span className="per">/ night</span>
                    </span>
                    {nights > 0 ? (
                      <span className="total">
                        {formatPrice(total, l.currency, displayCurrency)} for {nights} nights · taxes included
                      </span>
                    ) : null}
                  </div>
                  <div className="row-actions">
                    {l.cancellationPolicy === "flexible" ? (
                      <span className="pill cancel">Free cancellation</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>
      <aside
        className="map-panel"
        style={mapHeightPx != null ? { height: `${mapHeightPx}px` } : undefined}
      >
        <SearchMap results={results} onBoundsChange={onBoundsChange} />
        {/* Drag handle — mobile-only via CSS. Tap to hide; drag up
            to shrink, drag down to expand. Snaps to hidden / normal /
            expanded on release. */}
        <button
          type="button"
          className="map-handle"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          aria-label="Hide map"
        >
          <span />
        </button>
      </aside>
    </div>
  );
}
